// /var/www/devit-dashboard/src/pages/Candidates.js
import { useState, useEffect } from 'react';
import { candidates as api, jobOffers as offersApi } from '../services/api';

const ESTADOS = ['pendiente', 'entrevista', 'descartado'];

const ESTADO_COLOR = {
  pendiente:  { bg: '#2a2a00', text: '#ffb347' },
  entrevista: { bg: '#003a2a', text: '#00d4a0' },
  descartado: { bg: '#2a2a2a', text: '#888'    },
};

function ScoreBar({ score }) {
  const color = score >= 80 ? '#00d4a0' : score >= 60 ? '#ffb347' : '#ff6b6b';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ flex: 1, height: '6px', background: 'var(--bg3)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: color, borderRadius: '3px', transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 700, color, minWidth: '32px' }}>{score}</span>
    </div>
  );
}

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export default function Candidates() {
  const [lista,     setLista]     = useState([]);
  const [ofertas,   setOfertas]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filters,   setFilters]   = useState({ oferta_id: '', estado: '' });
  const [updating,  setUpdating]  = useState(null);
  const [contactar, setContactar] = useState(null);   // candidato seleccionado
  const [contactMsg, setContactMsg] = useState(null); // { tipo, asunto?, cuerpo?, mensaje?, mailto? }
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [tipoContacto, setTipoContacto] = useState('email');

  useEffect(() => {
    offersApi.getAll(false)
      .then(d => setOfertas(Array.isArray(d) ? d : []))
      .catch(() => {});
    cargar();
  }, []); // eslint-disable-line

  const cargar = (f = filters) => {
    setLoading(true);
    api.getAll(f)
      .then(d => setLista(Array.isArray(d) ? d : []))
      .catch(() => setLista([]))
      .finally(() => setLoading(false));
  };

  const handleFilter = (campo, valor) => {
    const nuevo = { ...filters, [campo]: valor };
    setFilters(nuevo);
    cargar(nuevo);
  };

  const handleEstado = async (c, nuevoEstado) => {
    setUpdating(c.id);
    try {
      await api.update(c.id, { estado: nuevoEstado });
      setLista(prev => prev.map(x => x.id === c.id ? { ...x, estado: nuevoEstado } : x));
    } finally {
      setUpdating(null);
    }
  };

  const handleContactar = async (c) => {
    setContactar(c);
    setContactMsg(null);
    setTipoContacto('email');
  };

  const generarMensaje = async () => {
    if (!contactar) return;
    setLoadingMsg(true);
    try {
      const res = await api.contactar(contactar.id, tipoContacto);
      setContactMsg(res);
    } catch {
      setContactMsg({ error: 'Error al generar el mensaje. Inténtalo de nuevo.' });
    } finally {
      setLoadingMsg(false);
    }
  };

  // Contadores
  const totales = ESTADOS.reduce((acc, e) => {
    acc[e] = lista.filter(c => c.estado === e).length;
    return acc;
  }, {});

  return (
    <div className="page" style={{ padding: '32px', maxWidth: '1050px' }}>

      {/* ── Cabecera ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Candidatos</h1>
        <p style={{ color: 'var(--text2)', marginTop: '6px', fontSize: '14px' }}>
          Candidatos cualificados por el agente. Gestiona el estado y contacta con un clic.
        </p>
      </div>

      {/* ── Badges resumen ───────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {ESTADOS.map(e => {
          const ec = ESTADO_COLOR[e];
          return (
            <div key={e} style={{
              padding: '6px 14px', borderRadius: '20px',
              background: ec.bg, border: '1px solid var(--border)',
              fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ color: ec.text, fontWeight: 600, textTransform: 'capitalize' }}>{e}</span>
              <span style={{ fontWeight: 700, color: 'var(--text1)' }}>{totales[e]}</span>
            </div>
          );
        })}
        <div style={{
          padding: '6px 14px', borderRadius: '20px', marginLeft: 'auto',
          background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: '13px', color: 'var(--text2)',
        }}>
          Total: <strong style={{ color: 'var(--text1)' }}>{lista.length}</strong>
        </div>
      </div>

      {/* ── Filtros ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select
          value={filters.oferta_id}
          onChange={e => handleFilter('oferta_id', e.target.value)}
          style={selectStyle}
        >
          <option value="">Todas las ofertas</option>
          {ofertas.map(o => <option key={o.id} value={o.id}>{o.titulo}</option>)}
        </select>

        <select
          value={filters.estado}
          onChange={e => handleFilter('estado', e.target.value)}
          style={selectStyle}
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
        </select>

        <button
          onClick={() => { setFilters({ oferta_id: '', estado: '' }); cargar({ oferta_id: '', estado: '' }); }}
          style={btnStyle}
        >
          Limpiar
        </button>
      </div>

      {/* ── Tabla ────────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: 'var(--text2)' }}>Cargando candidatos...</p>
      ) : lista.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px',
          padding: '48px', textAlign: 'center', color: 'var(--text2)', fontSize: '14px',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>👤</div>
          <p style={{ margin: 0 }}>
            {filters.oferta_id || filters.estado
              ? 'No hay candidatos con los filtros seleccionados.'
              : 'Aún no hay candidatos. Cuando alguien aplique por WhatsApp aparecerá aquí.'}
          </p>
        </div>
      ) : (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
          {/* Cabecera */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 140px 90px 130px 110px 100px',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
            fontSize: '11px', color: 'var(--text2)', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            <span>Candidato</span>
            <span>Oferta</span>
            <span>Score</span>
            <span>Fecha</span>
            <span>Estado</span>
            <span></span>
          </div>

          {lista.map((c, i) => {
            const ec = ESTADO_COLOR[c.estado] || ESTADO_COLOR.pendiente;
            return (
              <div
                key={c.id}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 140px 90px 130px 110px 100px',
                  padding: '13px 16px', alignItems: 'center',
                  borderBottom: i < lista.length - 1 ? '1px solid var(--border)' : 'none',
                  fontSize: '13px',
                }}
              >
                {/* Candidato */}
                <div>
                  <div style={{ fontWeight: 600 }}>{c.nombre || '—'}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '2px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {c.telefono && <span>📞 {c.telefono}</span>}
                    {c.correo   && <span>✉ {c.correo}</span>}
                    {c.oferta_sugerida_titulo && (
                      <span style={{ color: '#a78bfa' }}>💡 Cross: {c.oferta_sugerida_titulo}</span>
                    )}
                  </div>
                </div>

                {/* Oferta */}
                <div style={{ fontSize: '12px', color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.oferta_titulo || '—'}
                </div>

                {/* Score */}
                <div style={{ paddingRight: '8px' }}>
                  <ScoreBar score={c.cv_score || 0} />
                </div>

                {/* Fecha */}
                <span style={{ fontSize: '11px', color: 'var(--text2)' }}>
                  {formatFecha(c.created_at)}
                </span>

                {/* Estado */}
                <select
                  value={c.estado}
                  disabled={updating === c.id}
                  onChange={e => handleEstado(c, e.target.value)}
                  style={{
                    padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                    border: 'none', cursor: 'pointer', opacity: updating === c.id ? 0.5 : 1,
                    background: ec.bg, color: ec.text,
                  }}
                >
                  {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>

                {/* Contactar */}
                <button
                  onClick={() => handleContactar(c)}
                  style={{
                    padding: '6px 12px', borderRadius: '7px', fontSize: '12px', fontWeight: 600,
                    border: 'none', background: '#14b8a6', color: '#fff', cursor: 'pointer',
                  }}
                >
                  Contactar
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal: Contactar ─────────────────────────────────────── */}
      {contactar && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Contactar candidato</h2>
              <button onClick={() => { setContactar(null); setContactMsg(null); }}
                style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: '20px' }}>×</button>
            </div>

            {/* Info candidato */}
            <div style={{ background: 'var(--bg3)', borderRadius: '10px', padding: '14px', marginBottom: '20px', fontSize: '13px' }}>
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>{contactar.nombre || 'Candidato sin nombre'}</div>
              <div style={{ color: 'var(--text2)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {contactar.telefono && <span>📞 {contactar.telefono}</span>}
                {contactar.correo   && <span>✉ {contactar.correo}</span>}
                <span>Score: <strong style={{ color: contactar.cv_score >= 65 ? '#00d4a0' : '#ff6b6b' }}>{contactar.cv_score}/100</strong></span>
              </div>
            </div>

            {/* Selector tipo */}
            <div style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Tipo de mensaje:</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['email', 'mensaje'].map(t => (
                  <button key={t} onClick={() => { setTipoContacto(t); setContactMsg(null); }} style={{
                    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    border: '1px solid var(--border)', cursor: 'pointer',
                    background: tipoContacto === t ? 'var(--purple)' : 'var(--bg3)',
                    color: tipoContacto === t ? '#fff' : 'var(--text2)',
                  }}>
                    {t === 'email' ? '✉ Email' : '💬 WhatsApp'}
                  </button>
                ))}
              </div>
            </div>

            {/* Botón generar */}
            {!contactMsg && (
              <button onClick={generarMensaje} disabled={loadingMsg} style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: loadingMsg ? 'var(--bg3)' : 'var(--purple)',
                color: '#fff', fontWeight: 700, cursor: loadingMsg ? 'not-allowed' : 'pointer',
                fontSize: '13px', width: '100%', marginBottom: '16px',
              }}>
                {loadingMsg ? '⏳ Generando...' : '✨ Generar mensaje con IA'}
              </button>
            )}

            {/* Resultado */}
            {contactMsg && !contactMsg.error && (
              <div style={{ background: 'var(--bg3)', borderRadius: '10px', padding: '16px' }}>
                {contactMsg.tipo === 'email' && contactMsg.asunto && (
                  <div style={{ marginBottom: '12px' }}>
                    <p style={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 4px' }}>Asunto:</p>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '13px' }}>{contactMsg.asunto}</p>
                  </div>
                )}
                <p style={{ fontSize: '11px', color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 6px' }}>
                  {contactMsg.tipo === 'email' ? 'Cuerpo:' : 'Mensaje:'}
                </p>
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.7', whiteSpace: 'pre-line' }}>
                  {contactMsg.cuerpo || contactMsg.mensaje}
                </p>

                <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                  {contactMsg.mailto && (
                    <a href={contactMsg.mailto} style={{
                      padding: '9px 16px', borderRadius: '8px', background: '#14b8a6',
                      color: '#fff', fontWeight: 700, fontSize: '13px', textDecoration: 'none',
                    }}>
                      ✉ Abrir en email
                    </a>
                  )}
                  <button
                    onClick={() => navigator.clipboard.writeText(contactMsg.cuerpo || contactMsg.mensaje)}
                    style={{
                      padding: '9px 16px', borderRadius: '8px', border: '1px solid var(--border)',
                      background: 'var(--bg2)', color: 'var(--text2)', fontWeight: 600,
                      fontSize: '13px', cursor: 'pointer',
                    }}
                  >
                    📋 Copiar
                  </button>
                  <button onClick={() => setContactMsg(null)} style={{
                    padding: '9px 16px', borderRadius: '8px', border: '1px solid var(--border)',
                    background: 'var(--bg2)', color: 'var(--text2)', fontSize: '13px', cursor: 'pointer',
                  }}>
                    ↺ Regenerar
                  </button>
                </div>
              </div>
            )}

            {contactMsg?.error && (
              <p style={{ color: '#ff6b6b', fontSize: '13px' }}>{contactMsg.error}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Estilos ────────────────────────────────────────────────────────────────
const selectStyle = {
  padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text1)', fontSize: '13px', cursor: 'pointer',
};

const btnStyle = {
  padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text2)', fontSize: '13px', cursor: 'pointer',
};

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
};

const modalStyle = {
  background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '16px',
  padding: '28px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto',
};
